/**
 * Squish Deli home page behaviour (sections/sd-home-*.liquid). Loaded by snippets/sd-home-assets.liquid.
 *
 *   [data-sdh-when="pre maybe late after"]  Christmas phase gate (BUILD-SPEC 5, America/New_York):
 *        pre   until Sun Nov 22    "order by Sun Nov 22 · est. arrival by Dec 17"
 *        maybe Mon Nov 23-Tue Nov 24 "may not arrive before Dec 25"
 *        late  Wed Nov 25-Fri Dec 25 "will most likely arrive after Christmas" + reveal card
 *        after from Sat Dec 26     normal lines
 *   [data-sdh-offer]                        shown until Mon Nov 30 (opening offer)
 *   [data-sdh-menu]                         counter chips filter the menu board; "see the whole menu" on phones
 *   [data-sdh-video]                        silent loops: play in view only, pause button, poster under reduced motion
 *   [data-sdh-plate-status]                 live "N on your plate" line from the cart
 *
 * Liquid renders the right phase first; this file corrects cached HTML and powers ?sd_date=YYYY-MM-DD QA.
 */
import { nyToday, formatMoney } from '@theme/sd-ui';
import { StandardEvents } from '@shopify/events';

const DATES = Object.freeze({
  maybeStart: '2026-11-23',
  lateStart: '2026-11-25',
  afterStart: '2026-12-26',
  offerEnd: '2026-10-19', // opening offer ended early: the Mini was dropped (2026-10-20)
});

/** @param {Date} [today] */
export function homePhase(today = nyToday()) {
  const t = today.toISOString().slice(0, 10);
  let phase = 'pre';
  if (t >= DATES.afterStart) phase = 'after';
  else if (t >= DATES.lateStart) phase = 'late';
  else if (t >= DATES.maybeStart) phase = 'maybe';
  return { phase, offer: t <= DATES.offerEnd, today: t };
}

/** @param {ParentNode} root */
function applyHomePhase(root) {
  const { phase, offer } = homePhase();
  for (const el of root.querySelectorAll('[data-sdh-when]')) {
    if (!(el instanceof HTMLElement)) continue;
    const wanted = (el.dataset.sdhWhen || '').split(/\s+/).filter(Boolean);
    el.hidden = !wanted.includes(phase);
  }
  for (const el of root.querySelectorAll('[data-sdh-offer]')) {
    if (el instanceof HTMLElement) el.hidden = !offer;
  }
}

/* ------------------------------------------------------------------ */
/* Menu board: counter chips + "see the whole menu" on phones          */
/* ------------------------------------------------------------------ */

/** @param {ParentNode} root */
function initMenus(root) {
  for (const menu of root.querySelectorAll('[data-sdh-menu]')) {
    if (!(menu instanceof HTMLElement) || menu.dataset.sdhReady) continue;
    menu.dataset.sdhReady = 'true';

    const chips = [...menu.querySelectorAll('[data-sdh-filter]')];
    const items = [...menu.querySelectorAll('[data-sdh-item]')];
    const more = menu.querySelector('[data-sdh-more]');
    const status = menu.querySelector('[data-sdh-status]');

    /** @param {string} filter @param {string} label */
    const apply = (filter, label) => {
      let shown = 0;
      for (const chip of chips) chip.setAttribute('aria-pressed', String(chip.getAttribute('data-sdh-filter') === filter));
      for (const item of items) {
        if (!(item instanceof HTMLElement)) continue;
        const match = filter === 'all' || item.dataset.sdhCounter === filter || item.dataset.sdhKind === 'promo';
        item.hidden = !match;
        if (match && item.dataset.sdhKind !== 'promo') shown += 1;
      }
      menu.classList.toggle('is-filtered', filter !== 'all');
      if (status) status.textContent = filter === 'all' ? `Showing the whole menu, ${shown} items.` : `Showing ${label}, ${shown} items.`;
    };

    for (const chip of chips) {
      chip.addEventListener('click', () => {
        apply(chip.getAttribute('data-sdh-filter') || 'all', chip.getAttribute('data-sdh-label') || '');
        chip.scrollIntoView({ block: 'nearest', inline: 'nearest', behavior: reduceMotion.matches ? 'auto' : 'smooth' });
      });
    }

    more?.addEventListener('click', () => {
      const firstHidden = items.find((item) => item instanceof HTMLElement && item.offsetParent === null && !item.hidden);
      menu.classList.add('is-expanded');
      more.setAttribute('aria-expanded', 'true');
      const link = firstHidden?.querySelector('a[href]:not([tabindex="-1"])');
      if (link instanceof HTMLElement) link.focus({ preventScroll: true });
    });
  }
}

/* ------------------------------------------------------------------ */
/* Silent loops                                                         */
/* ------------------------------------------------------------------ */

const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
const wide = window.matchMedia('(min-width: 750px)');
let pageLoaded = document.readyState === 'complete';
const afterLoad = [];
if (!pageLoaded) {
  window.addEventListener(
    'load',
    () => {
      pageLoaded = true;
      const run = () => afterLoad.splice(0).forEach((fn) => fn());
      if ('requestIdleCallback' in window) window.requestIdleCallback(run, { timeout: 1200 });
      else setTimeout(run, 200);
    },
    { once: true }
  );
}

/** @param {ParentNode} root */
function initVideos(root) {
  for (const wrap of root.querySelectorAll('[data-sdh-video]')) {
    if (!(wrap instanceof HTMLElement) || wrap.dataset.sdhReady) continue;
    const video = wrap.querySelector('video');
    if (!(video instanceof HTMLVideoElement)) continue;
    wrap.dataset.sdhReady = 'true';

    const toggle = wrap.querySelector('[data-sdh-video-toggle]');
    const saveData = /** @type {any} */ (navigator).connection?.saveData === true;
    let paused = reduceMotion.matches || saveData;
    let inView = false;
    let ready = !wrap.hasAttribute('data-sdh-defer') || pageLoaded;
    let loadedSrc = '';

    const pickSrc = () => (wide.matches ? wrap.dataset.srcWide : wrap.dataset.srcNarrow) || wrap.dataset.srcWide || wrap.dataset.srcNarrow || '';

    const syncButton = () => {
      if (!(toggle instanceof HTMLElement)) return;
      const playing = !paused;
      toggle.setAttribute('aria-pressed', String(!playing));
      toggle.setAttribute('aria-label', playing ? toggle.dataset.labelPause || 'Pause video' : toggle.dataset.labelPlay || 'Play video');
      wrap.classList.toggle('is-paused', !playing);
    };

    const update = () => {
      syncButton();
      if (paused || !inView || !ready) {
        if (!video.paused) video.pause();
        return;
      }
      const src = pickSrc();
      if (!src) return;
      if (loadedSrc !== src) {
        loadedSrc = src;
        wrap.classList.remove('is-playing');
        video.src = src;
        video.load();
      }
      const attempt = video.play();
      if (attempt) attempt.catch(() => {});
    };

    video.muted = true;
    video.addEventListener('playing', () => wrap.classList.add('is-playing'));
    video.addEventListener('error', () => wrap.classList.remove('is-playing'));

    toggle?.addEventListener('click', () => {
      paused = !paused;
      if (!paused) ready = true;
      update();
    });

    if (!ready) {
      afterLoad.push(() => {
        ready = true;
        update();
      });
    }

    const io = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) inView = entry.isIntersecting;
        update();
      },
      { threshold: 0.2 }
    );
    io.observe(wrap);

    wide.addEventListener('change', () => {
      if (loadedSrc) update();
    });
    reduceMotion.addEventListener('change', () => {
      paused = reduceMotion.matches;
      update();
    });
    document.addEventListener('visibilitychange', () => {
      if (document.hidden && !video.paused) video.pause();
      else update();
    });
    syncButton();
  }
}

/* ------------------------------------------------------------------ */
/* Plate ladder: live line from the cart                               */
/* ------------------------------------------------------------------ */

/** @param {HTMLElement} el @param {number} count */
function renderPlateStatus(el, count) {
  const sizes = (el.dataset.sizes || '3,5,8')
    .split(',')
    .map((n) => parseInt(n, 10))
    .filter((n) => n > 0)
    .sort((a, b) => a - b);
  const names = (el.dataset.names || '').split('|');
  const prices = (el.dataset.prices || '').split(',').map((n) => parseInt(n, 10));
  const text = el.querySelector('[data-sdh-plate-text]');
  if (!text) return;

  const ladder = el.closest('[data-sdh-ladder]');
  let current = -1;
  sizes.forEach((size, i) => {
    if (count >= size) current = i;
  });
  if (ladder) {
    for (const card of ladder.querySelectorAll('[data-sdh-plate]')) {
      if (card instanceof HTMLElement) card.classList.toggle('is-current', current >= 0 && card.dataset.sdhPlate === String(sizes[current]));
    }
  }

  if (count <= 0) {
    el.hidden = true;
    return;
  }
  const piece = count === 1 ? 'piece' : 'pieces';
  const next = sizes.find((size) => size > count);
  const nextIndex = sizes.indexOf(next ?? -1);
  let line = `You have ${count} ${piece} on your plate.`;
  if (next) {
    const gap = next - count;
    const each = Number.isFinite(prices[nextIndex]) ? ` (${formatMoney(prices[nextIndex])} each)` : '';
    line += ` ${gap} more ${gap === 1 ? 'makes' : 'make'} a ${names[nextIndex] || `plate of ${next}`}${each}.`;
  } else if (current >= 0) {
    line += ` That's a ${names[current] || 'full plate'}: the best price per piece.`;
  }
  text.textContent = line;
  el.hidden = false;
}

let plateTimer = 0;
async function refreshPlates() {
  const targets = [...document.querySelectorAll('[data-sdh-plate-status]')];
  if (!targets.length) return;
  try {
    const res = await fetch(`${window.Theme?.routes?.cart_url || '/cart'}.js`, {
      headers: { Accept: 'application/json' },
      credentials: 'same-origin',
    });
    if (!res.ok) return;
    const cart = await res.json();
    for (const el of targets) {
      if (!(el instanceof HTMLElement)) continue;
      const skip = (el.dataset.exclude || '').split(',').filter(Boolean);
      const count = (cart.items || []).reduce((sum, item) => (skip.includes(item.handle) ? sum : sum + (item.quantity || 0)), 0);
      renderPlateStatus(el, count);
    }
  } catch {
    /* the line simply stays hidden */
  }
}

function wirePlates() {
  if (!document.querySelector('[data-sdh-plate-status]')) return;
  refreshPlates();
  if (window.SquishDeliHomePlates) return;
  window.SquishDeliHomePlates = true;
  document.addEventListener(StandardEvents.cartLinesUpdate, (event) => {
    const settle = /** @type {any} */ (event).promise;
    const later = () => {
      clearTimeout(plateTimer);
      plateTimer = window.setTimeout(refreshPlates, 150);
    };
    if (settle?.then) settle.then(later, later);
    else later();
  });
}

/* ------------------------------------------------------------------ */

/** @param {ParentNode} [root] */
function init(root = document) {
  applyHomePhase(root);
  initMenus(root);
  initVideos(root);
  wirePlates();
}

if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => init(), { once: true });
else init();

document.addEventListener('shopify:section:load', (event) => {
  if (event.target instanceof HTMLElement) init(event.target);
});
